import { ErrorModal } from '@app/components/ErrorModal';
import { Price } from '@app/components/Price';
import {
  useAddCartLineItem,
  useCartAndDeliveryDatesDataContext,
} from '@app/data/clients/cartCheckoutClient';
import { AddToCartButton } from '@app/pages/i/components/AddToCartButton';
import { DiscountPromo } from '@app/pages/i/components/DiscountPromo';
import { LoadingBoundary } from '@app/pages/i/components/LoadingBoundary';
import {
  CONFIGURATOR_FORM_ID,
  ProductConfigurator,
} from '@app/pages/i/components/ProductConfigurator';
import {
  AttributeName,
  ConfiguratorField,
  DSFormOnChangeCallback,
} from '@app/pages/i/components/ProductConfigurator/types';
import { getFieldsRequiringATCGuard } from '@app/pages/i/components/ProductConfigurator/utils';
import { ProductFeaturesReviews } from '@app/pages/i/components/ProductFeaturesReviews';
import {
  getProductSwitchCategoryName,
  ProductStyleSwitcher,
} from '@app/pages/i/components/ProductStyleSwitcher';
import { ProductTrustSignals } from '@app/pages/i/components/ProductTrustSignals/ProductTrustSignals';
import { getImageUrl } from '@app/utils/getImageUrl';
import {
  Alert,
  Box,
  constants,
  Skeleton,
  Stack,
} from '@redbubble/design-system';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import { useState } from 'react';
import type {
  CategorySet,
  DefaultInventoryItem,
  InventoryItem,
  Price as PriceType,
} from 'types/shop-bff/types';
import { ListingInfo } from '../ListingInfo';
import styles from '../PrimaryContentSection/PrimaryContentSection.module.scss';
import { VolumeDiscount } from '../VolumeDiscount';
import {
  AddLineItemError,
  getHighestPriorityAddItemErrorType,
  SizeGuideButtonExperience,
  sizeGuideButtonExperience,
} from './utils';

const PriceLoadingComponent = ({ percent }: PriceType['discount']) => (
  <Stack direction={constants.VERTICAL} alignItems="start" spacing="xxs">
    <Box style={{ marginTop: '6px' }}>
      <Skeleton height={24} width={150} animated />
    </Box>
    {percent > 0 && <Skeleton height={24} width={120} animated />}
  </Stack>
);

const MobilePriceLoadingComponent = ({ percent }: PriceType['discount']) => (
  <Stack direction={constants.VERTICAL} alignItems="start" spacing="xxs">
    <Skeleton height={30} width={120} animated />
    {percent > 0 && <Skeleton height={30} width={150} animated />}
  </Stack>
);
export interface ProductDetailsSectionProps {
  className?: string;
  currentInventoryItem: InventoryItem;
  onAddToCart: () => void;
  inventoryCategorySet: CategorySet;
  configuratorFields: ConfiguratorField[];
  onConfiguratorFormChange: DSFormOnChangeCallback;
  showColorConfigSeparately: boolean;
  availableProducts?: DefaultInventoryItem[] | null;
}

export const getDisclaimerLink = (locale: string) => {
  return `https://help.redbubble.com/hc/${
    locale === 'en' ? 'en-us' : locale
  }/articles/360042620271`;
};

export const ProductDetailsSection: React.FC<ProductDetailsSectionProps> = ({
  currentInventoryItem,
  inventoryCategorySet,
  availableProducts,
  onAddToCart,
  configuratorFields,
  showColorConfigSeparately,
  onConfiguratorFormChange,
}) => {
  const [{ userId }, setUserInfo] = useUserInfo<UserInfo>();
  const { defaultItem, categoryName } = inventoryCategorySet;
  const {
    price,
    volumeDiscount,
    productTypeId,
    workId,
    gaCode,
    hasSizeGuide,
    previewSet,
    stockStatus,
  } = currentInventoryItem;
  const { work } = defaultItem;
  const productTitle = `${work.title} ${categoryName}`;

  const [isSubmitting, setSubmitting] = useState(false);
  const { dispatch, client } = useCartAndDeliveryDatesDataContext();
  const { addCartItem } = useAddCartLineItem({
    client,
    dispatch,
  });

  const [errorModalState, setErrorModalState] = useState<{
    open: boolean;
    type: 'retryable';
    onRetry: () => void;
  }>({
    open: false,
    type: 'retryable',
    onRetry: () => {},
  });

  const artworkUrl = previewSet?.previews.find(
    (p) => p.previewTypeId === 'artwork'
  )?.url;

  const handleAddToCartClick = async () => {
    setSubmitting(true);

    const { errors, cart } = await addCartItem({
      inventoryItemId: currentInventoryItem.id,
      quantity: 1,
    });

    if (!userId && cart) {
      setUserInfo((currentUserInfo) => ({
        ...currentUserInfo,
        userId: cart.user.id,
      }));
    }

    if (errors?.length) {
      const errorType = getHighestPriorityAddItemErrorType(errors);

      // TODO[PLC-2166] - handle item unavailable errors
      // TODO[PLC-2171] - handle invalid quantity errors

      if (errorType === AddLineItemError.Retryable) {
        setErrorModalState({
          open: true,
          type: 'retryable',
          onRetry: handleAddToCartClick,
        });
      }

      setSubmitting(false);
      return;
    }

    if (!errors?.length) {
      setSubmitting(false);
      onAddToCart();
      setErrorModalState({
        open: false,
        type: 'retryable',
        onRetry: () => {},
      });
    }
  };

  const isCategoryExistsInProductStyleSwitchCategories =
    !!getProductSwitchCategoryName(productTypeId);

  const currentValues = currentInventoryItem.attributes;
  const fieldsRequiringATCGuard = getFieldsRequiringATCGuard(
    currentInventoryItem.experiences,
    currentValues
  );

  const sizeGuiceButtonExperience = hasSizeGuide
    ? sizeGuideButtonExperience(configuratorFields)
    : null;

  // if there are no available volume discount thresholds
  // we want to hide the mobile price section to ensure an empty
  // box does not appear adding unnecessary space
  const sortedThresholds =
    (volumeDiscount &&
      price &&
      volumeDiscount.thresholds
        .slice()
        .sort((a, b) => a.quantity - b.quantity)
        .filter((discount) => discount.percentOff > price.discount.percent)) ||
    [];

  return (
    <>
      <Box className={styles.listingInfo}>
        <ListingInfo
          // TODO: When IHS schema updates, remove nullish option
          name={work.artistShopDisplayName ?? undefined}
          avatar={work.artist?.avatar}
          profileUrl={work.artist?.profileUrl}
          productTitle={productTitle}
        />
      </Box>
      {price && (
        <Box
          className={[styles.price, styles.desktopOnly]
            .filter(Boolean)
            .join(' ')}
          data-testid="product-details-price"
        >
          <LoadingBoundary
            loadingComponent={
              <PriceLoadingComponent
                percent={price.discount.percent}
                amount={price.discount.amount}
              />
            }
          >
            <Price
              amount={price.amount}
              currency={price.currency}
              discount={price.discount}
            />
          </LoadingBoundary>
        </Box>
      )}
      {volumeDiscount && price && sortedThresholds.length > 0 && (
        <Box className={styles.volumeDiscount}>
          <LoadingBoundary
            loadingComponent={<Skeleton height={26} width={200} animated />}
          >
            <div className={styles.desktopOnly}>
              <VolumeDiscount
                originalAmount={price.amount}
                currency={price.currency}
                thresholds={volumeDiscount.thresholds}
                salePercent={price.discount.percent}
              />
            </div>
            <div className={styles.mobileOnly}>
              <Alert
                intent={constants.INFORMATION}
                label={false}
                className={styles.mobileOnly}
              >
                <VolumeDiscount
                  originalAmount={price.amount}
                  currency={price.currency}
                  thresholds={volumeDiscount.thresholds}
                  salePercent={price.discount.percent}
                />
              </Alert>
            </div>
          </LoadingBoundary>
        </Box>
      )}
      {price?.discount && price.discount.percent > 0 && (
        <Box className={styles.discountPromo}>
          <LoadingBoundary
            loadingComponent={<Skeleton height={26} width={180} animated />}
          >
            <DiscountPromo discount={price?.discount.percent} />
          </LoadingBoundary>
        </Box>
      )}
      {Array.isArray(availableProducts) &&
        availableProducts.length > 0 &&
        isCategoryExistsInProductStyleSwitchCategories && (
          <Box className={styles.productStyleSwitcher}>
            <ProductStyleSwitcher
              currentProductTypeId={productTypeId}
              availableProducts={availableProducts}
              artistName={work.artist?.name || ''}
              matureContent={work.matureContent}
            />
          </Box>
        )}

      <Box
        className={`${styles.productConfigurator} ${
          configuratorFields.length > 0 ? '' : styles.hidden
        }`}
      >
        <ProductConfigurator
          formId={CONFIGURATOR_FORM_ID}
          analyticsCode={gaCode}
          currentValues={currentValues}
          configuratorFields={configuratorFields}
          onSubmit={handleAddToCartClick}
          fieldsRequiringATCGuard={fieldsRequiringATCGuard}
          currentProductTypeId={productTypeId}
          currentWorkId={workId}
          hasSizeGuide={hasSizeGuide}
          onChange={onConfiguratorFormChange}
          hiddenAttributes={
            showColorConfigSeparately
              ? [AttributeName.BodyColor, AttributeName.Color]
              : []
          }
          showSizeGuideButton={
            sizeGuiceButtonExperience === SizeGuideButtonExperience.Experience1
          }
        />
      </Box>
      <Box className={styles.addToCart} data-testid="add-to-cart-section">
        {price && (
          <Box className={styles.mobileOnly}>
            <LoadingBoundary
              loadingComponent={
                <MobilePriceLoadingComponent
                  percent={price.discount.percent}
                  amount={price.discount.amount}
                />
              }
            >
              <Price
                amount={price.amount}
                currency={price.currency}
                discount={price.discount}
                size="mediumSmall"
              />
            </LoadingBoundary>
          </Box>
        )}
        <Box className={styles.desktopOnly}>
          <LoadingBoundary loadingComponent={<Skeleton height={48} animated />}>
            <AddToCartButton
              loading={isSubmitting}
              formId={CONFIGURATOR_FORM_ID}
              size="xlarge"
              fontSize="display3"
              outOfStock={stockStatus.status === 'out-of-stock'}
            />
          </LoadingBoundary>
        </Box>
        <Box
          className={[styles.mobileOnly, styles.ATCButton]
            .filter(Boolean)
            .join(' ')}
          data-testid="mobile-add-to-cart"
        >
          <LoadingBoundary
            loadingComponent={<Skeleton height={42} width={152} animated />}
          >
            <AddToCartButton
              loading={isSubmitting}
              formId={CONFIGURATOR_FORM_ID}
              size="xlarge"
              fontSize="display5"
              hideIcon
              outOfStock={stockStatus.status === 'out-of-stock'}
            />
          </LoadingBoundary>
        </Box>
        <ErrorModal
          type={errorModalState.type}
          open={errorModalState.open}
          imageSrc={artworkUrl || getImageUrl('/icons/empty-cart.svg')}
          onRetry={errorModalState.onRetry}
          onClose={() =>
            setErrorModalState((prev) => ({ ...prev, open: false }))
          }
        />
      </Box>
      <ErrorBoundary>
        <Box className={styles.productTrustSignals}>
          <ProductTrustSignals
            workId={currentInventoryItem.workId}
            productTypeId={currentInventoryItem.productTypeId}
            showSizeGuideButton={
              sizeGuiceButtonExperience ===
              SizeGuideButtonExperience.Experience2
            }
            analyticsCode={gaCode}
          />
        </Box>
        <Box className={styles.productFeaturesReviews}>
          <ProductFeaturesReviews
            inventoryItemId={currentInventoryItem.id}
            productTypeId={currentInventoryItem.productTypeId}
          />
        </Box>
      </ErrorBoundary>
    </>
  );
};
